import { useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { DropdownMenu } from 'radix-ui';
import { breakpoints } from 'styles';
import { Button } from 'components/common';

import DropdownItem from './DropdownItem';
import { useFiltersDispatch } from './FilterContext';

import type { IconProp } from 'components/common/icons/Icon';
import type { StatCategory } from 'components/common/stats/StatCount';

type Props = {
  checked?: boolean | 'indeterminate';
  children: React.ReactNode;
  filterId: string;
  icon?: IconProp;
  itemsActiveCount?: number;
  itemsActiveCountCategory?: StatCategory;
  parentPortalId: string;
  portalId: string;
  onClear: () => void;
  onSubMenuClick?: () => void;
  setRootIsOpen: (isOpen: boolean) => void;
  title: string;
  titleSubtitle?: string | React.ReactNode;
  withCheckbox?: boolean;
};

const FilterButtonSubMenu = ({
  checked,
  children,
  filterId,
  icon,
  itemsActiveCount = 0,
  itemsActiveCountCategory,
  onClear,
  parentPortalId = 'filters-root',
  portalId,
  onSubMenuClick,
  setRootIsOpen,
  title,
  titleSubtitle,
  withCheckbox
}: Props) => {
  const portalContainer = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const { messages, theme } = useReduxState();

  const filterDispatch = useFiltersDispatch();

  const [active, setActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    portalContainer.current = document.createElement('div');
    portalContainer.current.setAttribute('id', portalId);

    const root = document.getElementById(parentPortalId) as HTMLDivElement;
    if (!root) return;
    root.appendChild(portalContainer.current);

    return () => {
      root.removeChild(portalContainer.current);
    };
  }, []);

  const handleApply = () => {
    filterDispatch({ type: 'submitFilters', filterId });
    setRootIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setActive(open);
    setIsOpen(open);
  };

  return (
    <DropdownMenu.Sub open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.SubTrigger
        asChild={true}
        onPointerMove={(ev) => ev.preventDefault()}
        onPointerLeave={(ev) => ev.preventDefault()}
      >
        {withCheckbox ? (
          <DropdownItem
            checked={checked}
            count={itemsActiveCount}
            countCategory="dark"
            label={title}
            labelSubtitle={titleSubtitle}
            onClick={(ev) => {
              ev.preventDefault();
              if (!isOpen) handleOpenChange(!isOpen);
              if (onSubMenuClick) onSubMenuClick();
            }}
            ref={triggerRef}
            showArrow={true}
            withCheckbox={true}
          />
        ) : (
          <DropdownItem
            count={itemsActiveCount}
            countCategory={itemsActiveCountCategory}
            forceActive={active}
            icon={icon}
            label={title}
            labelSubtitle={titleSubtitle}
            onClick={(ev) => {
              if (onSubMenuClick) {
                ev.preventDefault();
                onSubMenuClick();
                if (!isOpen) handleOpenChange(!isOpen);
              } else handleOpenChange(!isOpen);
            }}
            ref={triggerRef}
            showArrow={true}
          />
        )}
      </DropdownMenu.SubTrigger>

      <DropdownMenu.Portal container={portalContainer.current}>
        <DropdownMenu.SubContent
          className="vuo-filters-sub-content"
          hideWhenDetached={true}
          loop={true}
          sideOffset={3}
        >
          <div className="header">
            <DropdownMenu.Item
              asChild={true}
              onClick={(ev) => ev.preventDefault()}
              onPointerMove={(ev) => ev.preventDefault()}
              onPointerLeave={(ev) => ev.preventDefault()}
            >
              <Button
                category="lighter"
                icon="arrow-left"
                onClick={(ev) => {
                  ev.preventDefault();
                  handleOpenChange(false);
                  triggerRef?.current?.focus();
                }}
                size="small"
                text={messages.commonGoBack}
              />
            </DropdownMenu.Item>
          </div>

          <div className="content">{children}</div>

          <div className="footer">
            <DropdownMenu.Item
              asChild={true}
              onClick={(ev) => ev.preventDefault()}
              onPointerMove={(ev) => ev.preventDefault()}
              onPointerLeave={(ev) => ev.preventDefault()}
            >
              <Button
                category="lighter"
                onClick={() => onClear()}
                size="small"
                text={messages.commonClear}
              />
            </DropdownMenu.Item>

            <DropdownMenu.Item
              asChild={true}
              onPointerMove={(ev) => ev.preventDefault()}
              onPointerLeave={(ev) => ev.preventDefault()}
            >
              <Button
                category="success"
                onClick={handleApply}
                size="small"
                text={messages.filtersApply}
              />
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>

      <style jsx>{`
        .header {
          border-bottom: 1px solid ${theme.lightGreyDark};
          padding: 10px;
        }
        .footer {
          align-items: center;
          border-top: 1px solid ${theme.lightGreyDark};
          display: flex;
          flex-grow: 1;
          gap: 10px;
          justify-content: space-between;
          padding: 10px;
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .header {
            display: none;
          }
        }
      `}</style>

      <style jsx global>{`
        .vuo-filters-sub-content {
          background-color: white;
          border-radius: ${theme.dropdownBorderRadius};
          box-shadow: ${theme.dropdownShadow};
          max-width: 300px;
          min-width: 240px;
          width: 'fit-content';
          z-index: 35;

          animation-name: slideAndFade;
          animation-duration: 400ms;
          animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
          will-change: transform, opacity;
        }
        .vuo-filters-sub-content:not(:first-child) {
          margin-top: 5px;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .vuo-filters-sub-content {
            position: relative;
            left: calc(var(--radix-dropdown-menu-trigger-width) * -1);
          }
        }

        @keyframes slideAndFade {
          from {
            opacity: 0;
            transform: translate(-5px, -5px);
          }
          to {
            opacity: 1;
            transform: translate(0, 0);
          }
        }
      `}</style>
    </DropdownMenu.Sub>
  );
};

export default FilterButtonSubMenu;
